import { Fragment, useState, type FormEvent, type JSX } from "react";
import { Checkbox, Divider } from "@fluentui/react-components";
import { PersonRegular, LockClosedRegular, GlobeRegular } from "@fluentui/react-icons";
import { FluentInput, FluentButton } from "@/components/fluent-ui";
import { useAuth } from "@/services/auth/use-auth";
import { resolveLoginMethods, type LoginMethod } from "@/services/auth/auth-config";
import logo from "@/resources/logo.png";
import "./Login.scss";

export function Login(): JSX.Element {
  const { loginBasic, loginAzure, loginSso } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [activeLoginMethod, setActiveLoginMethod] = useState<LoginMethod | null>(null);
  const [error, setError] = useState("");

  // Resolved at render time so runtime config.js / tests can change settings.
  const methods = resolveLoginMethods();
  const primaryMethod = methods[0];
  const appUrl = window.APP_SETTINGS?.appUrl ?? "";
  const busy = activeLoginMethod !== null;

  const handleBasic = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setActiveLoginMethod("basic");
    try {
      await loginBasic(username, password, remember);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Invalid username or password.");
    } finally {
      setActiveLoginMethod(null);
    }
  };

  const handleAzure = async () => {
    setError("");
    setActiveLoginMethod("azureSso");
    try {
      await loginAzure();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Microsoft sign-in failed.");
    } finally {
      setActiveLoginMethod(null);
    }
  };

  const handleSso = async () => {
    setError("");
    setActiveLoginMethod("legacySso");
    try {
      await loginSso();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "TRIYO web SSO failed.");
    } finally {
      setActiveLoginMethod(null);
    }
  };

  const basicForm = (showOptions: boolean) => (
    <form className="login__form" onSubmit={handleBasic} noValidate>
      <FluentInput
        id="username"
        label="Username or email address"
        value={username}
        autoComplete="username"
        contentBefore={<PersonRegular />}
        onChange={(value) => {
          setUsername(value);
          setError("");
        }}
      />
      <FluentInput
        id="password"
        label="Password"
        type="password"
        value={password}
        autoComplete="current-password"
        contentBefore={<LockClosedRegular />}
        onChange={setPassword}
      />

      {showOptions ? (
        <div className="login__options">
          <Checkbox label="Remember me" checked={remember} onChange={(_e, data) => setRemember(!!data.checked)} />
          {appUrl ? (
            <a className="login__link" href={`${appUrl}forgot-password`} target="_blank" rel="noreferrer">
              Forgot password?
            </a>
          ) : null}
        </div>
      ) : null}

      <FluentButton
        text={activeLoginMethod === "basic" ? "Signing in…" : "Log in"}
        type="submit"
        loading={activeLoginMethod === "basic"}
        disabled={!username || !password || busy}
        className="login__submit"
      />
    </form>
  );

  const ssoBlock = (isPrimary: boolean) => {
    const button = (
      <FluentButton
        text={activeLoginMethod === "legacySso" ? "Waiting for web sign-in…" : "Log in via TRIYO Web (SSO)"}
        appearance={isPrimary ? "primary" : "secondary"}
        loading={activeLoginMethod === "legacySso"}
        disabled={busy}
        className="login__sso"
        icon={<GlobeRegular />}
        onClick={() => void handleSso()}
      />
    );
    if (!isPrimary) {
      return button;
    }
    return (
      <div className="login__sso-panel">
        <p className="login__sso-copy">
          Sign in through the TRIYO web application. Once you are authenticated there, this add-in will pick up your
          session automatically.
        </p>
        {button}
        <p className="login__sso-hint">A browser window will open. After signing in, you can close it and return here.</p>
      </div>
    );
  };

  const azureButton = (isPrimary: boolean) => (
    <FluentButton
      text={activeLoginMethod === "azureSso" ? "Signing in…" : "Sign in with Microsoft"}
      appearance={isPrimary ? "primary" : "secondary"}
      loading={activeLoginMethod === "azureSso"}
      disabled={busy}
      className="login__azure"
      onClick={() => void handleAzure()}
    />
  );

  const sectionFor = (method: LoginMethod): JSX.Element => {
    switch (method) {
      case "basic":
        // Legacy parity: the SSO-first layout renders a compact form without
        // the Remember me / Forgot password row.
        return basicForm(primaryMethod !== "legacySso");
      case "legacySso":
        return ssoBlock(method === primaryMethod);
      case "azureSso":
        return azureButton(method === primaryMethod);
    }
  };

  const dividerLabel = (method: LoginMethod): string =>
    method === "basic" && primaryMethod === "legacySso" ? "or use username / password" : "or";

  return (
    <div className="login">
      <img src={logo} className="login__logo" alt="TRIYO" />
      <h2 className="login__title">Sign in to TRIYO</h2>

      {error ? (
        <div className="login__error" role="alert">
          {error}
        </div>
      ) : null}

      {methods.map((method, index) => (
        <Fragment key={method}>
          {index > 0 ? <Divider className="login__divider">{dividerLabel(method)}</Divider> : null}
          {sectionFor(method)}
        </Fragment>
      ))}
    </div>
  );
}
